import { cancelAccountQueries, clearAccountCaches } from "./accountCaches";
import { setActiveWorkspaceId } from "./activeWorkspace";
/**
 * #3005 — switch between the accounts signed in on this browser.
 *
 * Clerk's multi-session mode keeps several sessions in one browser; the
 * dev shim keeps a list of the dev ids this browser used. Either way a
 * switch runs the same steps, in this order (`runAccountSwitch`):
 *
 *   1. A full-screen cover, "Switching to ‹name›": nothing of either
 *      account shows under it.
 *   2. Cancel the queries in flight.
 *   3. `clearAccountCaches()` — react-query and the worker cache (#2919).
 *   4. Clear the workspace pointer, the dev-shim id and the tab's return
 *      stash: the next account resolves its own home.
 *   5. Activate the other account (Clerk `setActive`, or the dev id).
 *   6. `clearAccountCaches()` again, for a refill in the gap.
 *   7. Load `/` in full: memory, stores and streams start fresh, and the
 *      entry gates send the person to that account's home.
 *
 * Clerk's `getToken` keeps one identity per Clerk instance in
 * clerk-react, so nothing short of a full load is safe after a change of
 * session.
 *
 * Other tabs (founder D2 a, 1 Nov 2026): one account per browser. A tab
 * that sees a switch made elsewhere follows it at its next safe moment —
 * the #2919 rule: at once when nobody types, else on the next page change
 * or when the tab is hidden — and then says "Now signed in as ‹email›".
 *
 * The list never calls the API as an account that is not active: that
 * would stamp the account's last-active time. Names come from the
 * account's last load in this browser (`rememberAccountName`).
 */
import { isUserTyping } from "./appUpdate";
import { clearDevAccountId } from "./auth";
import { consumeReturnTo } from "./returnTo";

/** Where **Add another account** opens. */
export const ADD_ACCOUNT_PATH = "/sign-in/add";
/** Where **Choose an account** lives (after one account signs out, others stay). */
export const CHOOSE_ACCOUNT_PATH = "/sign-in/choose";

/** Dev builds only: the dev ids this browser used, with name and email. */
export const DEV_RECENT_ACCOUNTS_KEY = "tarii.dev_recent_accounts";
/** Clerk user id → the Tarii account name from its last load here. */
export const ACCOUNT_NAMES_KEY = "tarii.account_names";
/** Written by the tab that switched; other tabs read it from the `storage` event. */
export const SWITCH_MESSAGE_KEY = "tarii.account_switched";
/** Per tab: the line to show once after a tab followed a switch. */
export const NOW_SIGNED_IN_KEY = "tarii.now_signed_in_as";
/** Per tab: Add another account left for Google from this session (`markAccountAddPending`). */
export const ADD_PENDING_KEY = "tarii.account_add_pending";
/** A Google add mark older than this is stale and dropped. */
const ADD_PENDING_MS = 10 * 60_000;

const COVER_ID = "tarii-account-switch-cover";
const DEV_SHIM_FLAG = "tarii.dev_shim_session_authorized";

/** One account signed in on this browser. */
export interface SignedInAccount {
  /** Clerk session id, or the dev-shim account id. */
  key: string;
  /** Tarii name from the last load here, else Clerk's full name, else the email. */
  name: string;
  email: string | null;
  /** The Clerk photo when the person has one; null shows initials. */
  imageUrl: string | null;
  current: boolean;
}

/** `current` signs out this account only; `all` every account on this browser. */
export type SignOutScope = "current" | "all";

let switching = false;
let followPending: string | null = null;
let hiddenListener: (() => void) | null = null;

/** True while this tab runs a switch (the session watch ignores its own change). */
export function isAccountSwitching(): boolean {
  return switching;
}

function loadPage(path: string): void {
  window.location.assign(path);
}

// ─── cover ─────────────────────────────────────────────────────────────

/** Step 1: a full-screen cover over everything, with one line. */
export function showSwitchCover(label: string): void {
  if (typeof document === "undefined") return;
  let cover = document.getElementById(COVER_ID);
  if (!cover) {
    cover = document.createElement("div");
    cover.id = COVER_ID;
    cover.setAttribute("role", "status");
    cover.setAttribute("data-testid", "account-switch-cover");
    cover.className =
      "fixed inset-0 z-[1000] flex items-center justify-center bg-bg px-6 text-center text-sm text-fg-muted";
    // Inline too, so the cover holds even before the stylesheet applies.
    cover.style.position = "fixed";
    cover.style.inset = "0";
    cover.style.zIndex = "1000";
    document.body.appendChild(cover);
  }
  cover.textContent = `Switching to ${label}`;
}

export function hideSwitchCover(): void {
  if (typeof document === "undefined") return;
  document.getElementById(COVER_ID)?.remove();
}

// ─── the switch ────────────────────────────────────────────────────────

/** Steps 2 to 4: nothing of the account leaving survives into the next one. */
export async function prepareAccountChange(opts: { keepDevId?: boolean } = {}): Promise<void> {
  await cancelAccountQueries();
  await clearAccountCaches();
  setActiveWorkspaceId(null);
  if (!opts.keepDevId) clearDevAccountId();
  consumeReturnTo();
}

/**
 * Run the seven steps. `label` names the account on the cover;
 * `activate` is step 5. A failed activation still ends in the full load:
 * the page comes back as whichever account Clerk holds, which is the
 * honest outcome.
 */
export async function runAccountSwitch(opts: {
  label: string;
  activate: () => Promise<void> | void;
  /** Dev shim: `activate` writes the new dev id itself. */
  keepDevId?: boolean;
  /** What other tabs say, "Now signed in as ‹email›" (default `label`). */
  announceAs?: string;
}): Promise<void> {
  switching = true;
  showSwitchCover(opts.label);
  // This switch tells the other tabs itself: no Google add mark may repeat it.
  clearAccountAddPending();
  try {
    await prepareAccountChange({ keepDevId: opts.keepDevId ?? false });
    try {
      await opts.activate();
      announceAccountSwitch(opts.announceAs || opts.label);
    } catch (err) {
      console.error("Account switch failed", err);
    }
    await clearAccountCaches();
  } finally {
    loadPage("/");
  }
}

/** Where a sign-out lands: Choose an account while another account stays signed in. */
export function afterSignOutPath(
  accounts: ReadonlyArray<SignedInAccount> | undefined,
  scope: SignOutScope,
  fallback = "/",
): string {
  if (scope === "all") return fallback;
  const othersRemain = (accounts ?? []).some((a) => !a.current);
  return othersRemain ? CHOOSE_ACCOUNT_PATH : fallback;
}

/**
 * The account menu's and the profile sheet's sign-out (#1520-hardened):
 * clear the dev id, AWAIT the revoke, then a full load of where the
 * sign-out lands — Choose an account while another account stays.
 */
export async function signOutAndLeave(
  signOut: (opts: { redirectUrl: string; scope: SignOutScope }) => Promise<void>,
  accounts: ReadonlyArray<SignedInAccount> | undefined,
  scope: SignOutScope,
): Promise<void> {
  const target = afterSignOutPath(accounts, scope);
  clearDevAccountId();
  try {
    await signOut({ redirectUrl: target, scope });
  } catch (err) {
    // Revoke failed: load anyway rather than dead-end in an open menu.
    console.error("Sign-out failed to revoke the session", err);
  } finally {
    loadPage(target);
  }
}

// ─── remembered names (Clerk) ──────────────────────────────────────────

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the list falls back to Clerk's names.
  }
}

/** Clerk user id → Tarii account name, for the accounts still signed in. */
export function readAccountNames(): Record<string, string> {
  const names = readJson<Record<string, unknown>>(ACCOUNT_NAMES_KEY, {});
  const out: Record<string, string> = {};
  for (const [id, name] of Object.entries(names)) {
    if (typeof name === "string" && name) out[id] = name;
  }
  return out;
}

/** Remember the active account's Tarii name for the list. */
export function rememberAccountName(userId: string, name: string): void {
  if (!userId || !name) return;
  const names = readAccountNames();
  if (names[userId] === name) return;
  writeJson(ACCOUNT_NAMES_KEY, { ...names, [userId]: name });
}

/**
 * Drop the names of accounts no longer signed in here (D1 a: a signed-out
 * account leaves no trace). No ids: forget every name.
 */
export function forgetAccountNames(keepUserIds: ReadonlyArray<string> = []): void {
  const names = readAccountNames();
  const kept: Record<string, string> = {};
  for (const id of keepUserIds) {
    const name = names[id];
    if (name) kept[id] = name;
  }
  if (Object.keys(kept).length === Object.keys(names).length) return;
  if (Object.keys(kept).length === 0) {
    try {
      window.localStorage.removeItem(ACCOUNT_NAMES_KEY);
    } catch {
      // Storage blocked: nothing was stored.
    }
    return;
  }
  writeJson(ACCOUNT_NAMES_KEY, kept);
}

// ─── dev shim recent accounts ──────────────────────────────────────────

export interface DevRecentAccount {
  id: string;
  name: string;
  email: string | null;
}

export function readDevRecentAccounts(): DevRecentAccount[] {
  const rows = readJson<unknown>(DEV_RECENT_ACCOUNTS_KEY, []);
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (r): r is DevRecentAccount =>
      !!r && typeof r === "object" && typeof (r as DevRecentAccount).id === "string",
  );
}

/** Record the loaded dev account at the top of the list. */
export function noteDevRecentAccount(row: DevRecentAccount): void {
  const rest = readDevRecentAccounts().filter((r) => r.id !== row.id);
  writeJson(DEV_RECENT_ACCOUNTS_KEY, [row, ...rest].slice(0, 12));
}

export function forgetDevRecentAccount(id: string): void {
  writeJson(
    DEV_RECENT_ACCOUNTS_KEY,
    readDevRecentAccounts().filter((r) => r.id !== id),
  );
}

export function clearDevRecentAccounts(): void {
  try {
    window.localStorage.removeItem(DEV_RECENT_ACCOUNTS_KEY);
  } catch {
    // Storage blocked: nothing was stored.
  }
}

/** Dev shim, step 5: the new dev id survives the full load (the `/demo` picker's flag). */
export function activateDevAccount(id: string, write: (id: string) => void): void {
  write(id);
  try {
    window.sessionStorage.setItem(DEV_SHIM_FLAG, "1");
  } catch {
    // Storage blocked: the full load comes back signed out.
  }
}

// ─── other tabs (D2 a) ─────────────────────────────────────────────────

/** Tell the other tabs of this browser that the account changed. */
export function announceAccountSwitch(label: string): void {
  writeJson(SWITCH_MESSAGE_KEY, { label, at: Date.now() });
}

function applyFollow(): boolean {
  if (followPending === null || isUserTyping()) return false;
  const label = followPending;
  followPending = null;
  detachHiddenListener();
  // The same steps as a switch, short of `setActive`: the cover first, so
  // nothing repaints while the caches clear. The dev id stays: the tab
  // that switched already wrote the new one to shared storage.
  switching = true;
  showSwitchCover(label);
  try {
    window.sessionStorage.setItem(NOW_SIGNED_IN_KEY, label);
  } catch {
    // Storage blocked: the page still loads as the new account.
  }
  void prepareAccountChange({ keepDevId: true }).finally(() => loadPage("/"));
  return true;
}

function detachHiddenListener(): void {
  if (!hiddenListener) return;
  document.removeEventListener("visibilitychange", hiddenListener);
  hiddenListener = null;
}

/**
 * The account changed somewhere else (another tab, or Clerk). Follow it
 * at the next safe moment: at once when nobody types, else on the next
 * page change (`noteRouteChangeForAccountSwitch`) or when the tab is
 * hidden. Never while the person types.
 */
export function followAccountSwitch(label: string): void {
  if (switching) return;
  followPending = label;
  if (applyFollow()) return;
  if (!hiddenListener && typeof document !== "undefined") {
    hiddenListener = () => {
      if (document.visibilityState === "hidden") applyFollow();
    };
    document.addEventListener("visibilitychange", hiddenListener);
  }
}

/**
 * Listen for a switch made in another tab of this browser (the `storage`
 * event on `SWITCH_MESSAGE_KEY`). Returns the stop function.
 */
export function startAccountSwitchFollower(): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key !== SWITCH_MESSAGE_KEY || !event.newValue) return;
    try {
      const message = JSON.parse(event.newValue) as { label?: unknown };
      if (typeof message.label !== "string") return;
      // The tab that switched told everyone: an old Google add mark here
      // must not send a second message after this tab's load.
      clearAccountAddPending();
      followAccountSwitch(message.label);
    } catch {
      // A malformed message: nothing to follow.
    }
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** The in-app page changed: a safe moment to follow a switch made elsewhere. */
export function noteRouteChangeForAccountSwitch(): void {
  applyFollow();
}

/**
 * Add another account with Google leaves the page and comes back through
 * `/sso-callback`, so `runAccountSwitch` never runs there. Before the
 * redirect, mark the tab with the session it left from. When an account
 * loads under another session, `takeAccountAddPending` says to tell the
 * other tabs (D2 a).
 */
export function markAccountAddPending(fromKey: string | null): void {
  try {
    window.sessionStorage.setItem(
      ADD_PENDING_KEY,
      JSON.stringify({ from: fromKey, at: Date.now() }),
    );
  } catch {
    // Storage blocked: other tabs follow at their next load instead.
  }
}

export function clearAccountAddPending(): void {
  try {
    window.sessionStorage.removeItem(ADD_PENDING_KEY);
  } catch {
    // Storage blocked: nothing was stored.
  }
}

/**
 * True once, when the account now loaded has another session than the
 * one the Google add left from. The same session (Google cancelled, or
 * the callback has not finished yet) keeps the mark. A stale mark goes.
 */
export function takeAccountAddPending(currentKey: string): boolean {
  let mark: { from?: unknown; at?: unknown } | null = null;
  try {
    const raw = window.sessionStorage.getItem(ADD_PENDING_KEY);
    mark = raw ? (JSON.parse(raw) as { from?: unknown; at?: unknown }) : null;
  } catch {
    clearAccountAddPending();
    return false;
  }
  if (!mark) return false;
  const at = typeof mark.at === "number" ? mark.at : 0;
  if (Date.now() - at > ADD_PENDING_MS) {
    clearAccountAddPending();
    return false;
  }
  if (mark.from === currentKey) return false;
  clearAccountAddPending();
  return true;
}

/** The "Now signed in as ‹email›" line, once per follow. */
export function consumeNowSignedInAs(): string | null {
  try {
    const label = window.sessionStorage.getItem(NOW_SIGNED_IN_KEY);
    if (label !== null) window.sessionStorage.removeItem(NOW_SIGNED_IN_KEY);
    return label;
  } catch {
    return null;
  }
}

/** Test seam: reset module state. */
export function __resetAccountSwitchForTests(): void {
  switching = false;
  followPending = null;
  detachHiddenListener();
  hideSwitchCover();
}
